import constate from 'constate';
import { lowerFirst } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { JobStatus } from 'cli/enums';
import { useJobChecker } from 'cli/hooks';
import { cliJobService } from 'cli/services';
import { ICliJob } from 'cli/types';
import { NotificationPosition } from 'core/enums';
import { ICliJobLostPoll } from 'core/hooks';
import { useAgGridTableContext, useNotificationContext } from 'core/states';
import { QuoteEntityType, QuoteVersionType } from 'quotes/enums';
import { useCustomerQuotePartsContext } from 'quotes/states';

import { refetchPendingVersioningCliJob, VersioningCliJob } from './versioningCliJobs';

interface IProps {
  quoteId: number;
  entityType?: QuoteEntityType;
}

const useQuoteVersioning = ({ quoteId, entityType = QuoteEntityType.QUOTE }: IProps) => {
  const queryClient = useQueryClient();

  const { t } = useTranslation();

  const [reloadList, setReloadList] = useState(false);
  const [releaseButtonDisabled, setReleaseButtonDisabled] = useState(false);
  const [resetButtonDisabled, setResetButtonDisabled] = useState(false);
  const [initDraftButtonDisabled, setInitDraftButtonDisabled] = useState(false);

  // Jobs this page polls from the details answer, so a refetch that reports one again does not
  // start a second poll.
  const resumedCliJobIdsRef = useRef(new Set<number>());

  const {
    clearNotification,
    showErrorNotification,
    showLoadingNotification,
    showWarningNotification,
  } = useNotificationContext();
  const { gridApi } = useAgGridTableContext();
  const { setDraftVersionChanged, setCurrentQuoteVersion } = useCustomerQuotePartsContext();
  const { checkJobStatus } = useJobChecker();

  useEffect(() => {
    if (reloadList) {
      queryClient.invalidateQueries('customerQuoteParts');

      setReloadList(false);
    }
  }, [queryClient, reloadList]);

  const showInterruptedError = (cliJob: ICliJob) =>
    showErrorNotification(
      {
        message: t('messageCliJobInterrupted', {
          description: cliJob.statusDescription || cliJob.status,
        }),
      },
      NotificationPosition.TOP
    );

  const onInitDraftSuccess = () => {
    onChangeVersionView(QuoteVersionType.DRAFT);

    setInitDraftButtonDisabled(false);
  };

  const onInitDraftError = (cliJob: ICliJob) => {
    showInterruptedError(cliJob);

    setInitDraftButtonDisabled(false);

    gridApi?.setGridOption('loading', false);
  };

  const onReleaseDraftSuccess = () => {
    onChangeVersionView(QuoteVersionType.RELEASE);

    setDraftVersionChanged(false);
    setReleaseButtonDisabled(false);

    queryClient.invalidateQueries('quote');
  };

  const onReleaseDraftError = (cliJob: ICliJob) => {
    gridApi?.setGridOption('loading', false);

    clearNotification();
    showInterruptedError(cliJob);
    setReleaseButtonDisabled(false);
  };

  const onResetDraftSuccess = () => {
    gridApi?.setGridOption('loading', false);

    clearNotification();
    setReloadList(true);
    setDraftVersionChanged(false);

    setResetButtonDisabled(false);
  };

  const onResetDraftError = (cliJob: ICliJob) => {
    gridApi?.setGridOption('loading', false);

    showInterruptedError(cliJob);

    setDraftVersionChanged(false);

    setResetButtonDisabled(false);
  };

  const versioningCliJobs = {
    [VersioningCliJob.INIT_DRAFT]: {
      inProgressMessage: t('messageQuoteDraftVersionInitializeInProgress'),
      onFinished: onInitDraftSuccess,
      onStopped: onInitDraftError,
      setButtonDisabled: setInitDraftButtonDisabled,
    },
    [VersioningCliJob.RELEASE_DRAFT]: {
      inProgressMessage: t('messageReleaseQuoteInProgress', { entityType: lowerFirst(entityType) }),
      onFinished: onReleaseDraftSuccess,
      onStopped: onReleaseDraftError,
      setButtonDisabled: setReleaseButtonDisabled,
    },
    [VersioningCliJob.RESET_DRAFT]: {
      inProgressMessage: t('messageQuoteVersionResetInProgress', {
        entityType: lowerFirst(entityType),
      }),
      onFinished: onResetDraftSuccess,
      onStopped: onResetDraftError,
      setButtonDisabled: setResetButtonDisabled,
    },
  };

  // The details answer still reports the job: back to the in-progress state, polling that job.
  const resumeVersioningCliJob = (job: VersioningCliJob, cliJob: ICliJob) => {
    const resumedCliJobIds = resumedCliJobIdsRef.current;

    if (!cliJob?.id || resumedCliJobIds.has(cliJob.id)) {
      return;
    }

    resumedCliJobIds.add(cliJob.id);

    const { inProgressMessage, onFinished, onStopped, setButtonDisabled } = versioningCliJobs[job];

    setButtonDisabled(true);
    gridApi?.setGridOption('loading', true);
    showLoadingNotification({ message: inProgressMessage }, NotificationPosition.TOP);

    checkJobStatus(
      cliJob,
      () => {
        resumedCliJobIds.delete(cliJob.id);
        onFinished();
      },
      (stoppedCliJob) => {
        resumedCliJobIds.delete(cliJob.id);
        onStopped(stoppedCliJob);
      },
      {
        notificationPosition: NotificationPosition.TOP,
        onError: () => {
          resumedCliJobIds.delete(cliJob.id);
          onVersioningPollFailed(job, cliJob, showLostTrackNotice);
        },
      }
    );
  };

  const showLostTrackNotice = () =>
    showWarningNotification({ message: t('messageCliJobLostTrack') }, NotificationPosition.TOP);

  // A job no longer reported may have finished or stopped: read how it ended. Null when the job's
  // details cannot be read either.
  const getEndedCliJob = async (cliJob: ICliJob): Promise<ICliJob | null> => {
    try {
      const { record } = await cliJobService.getDetails({ job: cliJob.id });

      return record ?? null;
    } catch {
      return null;
    }
  };

  // The job's status checks kept failing. The page stays in progress until the refetched details
  // tell: a job still reported resumes; otherwise the job's own status decides how it ended.
  const onVersioningPollFailed = async (
    job: VersioningCliJob,
    cliJob: ICliJob,
    onLostTrack: () => void
  ) => {
    const pendingCliJob = await refetchPendingVersioningCliJob(queryClient, quoteId, job);

    if (pendingCliJob) {
      resumeVersioningCliJob(job, pendingCliJob);

      return;
    }

    const { onFinished, onStopped, setButtonDisabled } = versioningCliJobs[job];

    // Null only means the job is no longer queued or running; undefined means the details answer
    // did not come back, so the job's end is unknown.
    const endedCliJob = null === pendingCliJob ? await getEndedCliJob(cliJob) : null;

    switch (endedCliJob?.status) {
      case JobStatus.FINISHED:
        onFinished();

        return;
      case JobStatus.FAILED:
      case JobStatus.CANCELED:
      case JobStatus.EXPIRED:
        onStopped(endedCliJob);

        return;
      case JobStatus.QUEUED:
      case JobStatus.RUNNING:
        resumeVersioningCliJob(job, endedCliJob);

        return;
      default:
        // Unlock without claiming the job's result; the notice says to check before retrying.
        gridApi?.setGridOption('loading', false);
        setButtonDisabled(false);
        onLostTrack();
    }
  };

  const onInitDraftPollFailed = ({ cliJob, showLostTrackNotice: onLostTrack }: ICliJobLostPoll) =>
    onVersioningPollFailed(VersioningCliJob.INIT_DRAFT, cliJob, onLostTrack);
  const onReleaseDraftPollFailed = ({
    cliJob,
    showLostTrackNotice: onLostTrack,
  }: ICliJobLostPoll) =>
    onVersioningPollFailed(VersioningCliJob.RELEASE_DRAFT, cliJob, onLostTrack);
  const onResetDraftPollFailed = ({ cliJob, showLostTrackNotice: onLostTrack }: ICliJobLostPoll) =>
    onVersioningPollFailed(VersioningCliJob.RESET_DRAFT, cliJob, onLostTrack);

  const onChangeVersionView = (versionType: QuoteVersionType) => {
    gridApi?.setGridOption('loading', true);

    clearNotification();

    setCurrentQuoteVersion(versionType);

    gridApi?.setGridOption('loading', false);
  };

  return {
    onInitDraftSuccess,
    onInitDraftError,
    onInitDraftPollFailed,
    onReleaseDraftSuccess,
    onReleaseDraftError,
    onReleaseDraftPollFailed,
    onResetDraftSuccess,
    onResetDraftError,
    onResetDraftPollFailed,
    onChangeVersionView,
    resumeVersioningCliJob,
    releaseButtonDisabled,
    setReleaseButtonDisabled,
    resetButtonDisabled,
    setResetButtonDisabled,
    initDraftButtonDisabled,
    setInitDraftButtonDisabled,
  };
};

export const [QuoteVersioningProvider, useQuoteVersioningContext] = constate(useQuoteVersioning);
